import { useState } from "react";
import { submitVolunteer } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Sparkles, Users, Sun, Send, CheckCircle2 } from "lucide-react";
import { useReveal } from "@/lib/useReveal";

const VOLUNTEERS_IMG = "https://static.prod-images.emergentagent.com/jobs/7c3bd71d-f3a1-4475-868e-2031ff56ad92/images/0da293f3e2da169b65ef1706c0ba1a148d5f9fb022de9609c007727662d20643.png";

const ROLES = [
  { icon: Heart, title: "Drop-in Companion", commit: "4h / week", text: "Welcome people into our meal and engagement hubs. No experience needed — just kindness." },
  { icon: Sparkles, title: "Laundry & Dignity Volunteer", commit: "Flexible", text: "Help run on-site and mobile laundry, hygiene kits, and showers — quietly, respectfully." },
  { icon: Users, title: "Mentor for Young People", commit: "2h / week", text: "Be a steady presence for a young person re-engaging with school, training, or work." },
  { icon: Sun, title: "Outreach Crew", commit: "Occasional", text: "Join the mobile food van or after-hours engagement crew. Predictable, friendly faces." },
];

const INTEREST_OPTIONS = [
  "Crisis accommodation",
  "Food & nutritional support",
  "Laundry & hygiene services",
  "Mental health & casework",
  "Mentorship & life skills",
  "After-hours engagement",
  "Education & employment pathways",
  "Events & community days",
];

export default function Volunteer() {
  useReveal();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    interests: [],
    availability: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const toggleInterest = (i) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(i)
        ? f.interests.filter((x) => x !== i)
        : [...f.interests, i],
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email so we can reach you.");
      return;
    }
    setSubmitting(true);
    try {
      await submitVolunteer(form);
      toast.success("Thank you — we'll be in touch within 48 hours.");
      setDone(true);
      setForm({ name: "", email: "", phone: "", city: "", interests: [], availability: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="volunteer-page">
      <section className="ripple-bg">
        <div className="container-app pt-24 sm:pt-32 pb-20">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 reveal">
              <p className="eyebrow">Volunteer with CLEAN Water Drop Association</p>
              <h1 className="font-heading mt-6 text-[44px] sm:text-[64px] lg:text-[76px] leading-[0.98] tracking-[-0.02em] font-semibold text-[#0A2540]">
                Join the people behind our services.
              </h1>
            </div>
            <div className="lg:col-span-4 reveal">
              <p className="text-lg text-slate-600 leading-relaxed">
                Volunteers help us run meals, laundry, hygiene services, mentorship sessions and
                after-hours engagement. Training is provided. Roles are flexible.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="container-app pb-16">
        <div className="relative aspect-[21/9] rounded-[2.5rem] overflow-hidden border border-slate-100 reveal">
          <img src={VOLUNTEERS_IMG} alt="A single drop of water creating ripples" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/35 via-transparent to-transparent" />
          <div className="absolute bottom-8 left-8 right-8 sm:bottom-12 sm:left-12 sm:right-12">
            <p className="font-heading text-white text-xl sm:text-2xl leading-snug max-w-2xl drop-shadow-md">
              Steady volunteers, consistent presence — that's how trust is built.
            </p>
          </div>
        </div>
      </section>

      <section className="section-y bg-[#F8FAFC]">
        <div className="container-app">
          <div className="max-w-2xl reveal">
            <p className="eyebrow">Volunteer roles</p>
            <h2 className="font-heading mt-4 text-3xl sm:text-4xl text-[#0A2540] leading-tight">
              Find your way in.
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {ROLES.map((r, i) => (
              <div
                key={r.title}
                className="card-soft reveal flex gap-5 items-start"
                style={{ transitionDelay: `${i * 60}ms` }}
                data-testid={`volunteer-role-${r.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span className="grid h-14 w-14 place-items-center rounded-2xl bg-[#E6F0F2] text-[#2E8B8B] flex-shrink-0">
                  <r.icon className="h-6 w-6" />
                </span>
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-heading text-xl text-[#0A2540]">{r.title}</h3>
                    <span className="rounded-full bg-white border border-slate-200 px-3 py-1 text-xs text-slate-600">{r.commit}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{r.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-y" id="signup">
        <div className="container-app">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 reveal">
              <p className="eyebrow">Sign up</p>
              <h2 className="font-heading mt-4 text-3xl sm:text-4xl text-[#0A2540] leading-tight">
                Tell us a little about you.
              </h2>
              <p className="mt-5 text-base text-slate-600 leading-relaxed">
                We'll never share your details. A coordinator from your nearest community
                will reach out within 48 hours with a warm welcome and next steps.
              </p>
              <ul className="mt-8 space-y-3 text-sm text-slate-600">
                {[
                  "Free training for every volunteer role",
                  "Trauma-informed onboarding",
                  "Flexible schedules — your life comes first",
                  "Real community, real friendships",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#2E8B8B]" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-7 reveal">
              {done ? (
                <div className="card-soft text-center py-16" data-testid="volunteer-success">
                  <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#E6F0F2] text-[#2E8B8B]">
                    <CheckCircle2 className="h-8 w-8" />
                  </span>
                  <h3 className="font-heading mt-6 text-2xl text-[#0A2540]">Welcome to the circle.</h3>
                  <p className="mt-3 text-base text-slate-600 max-w-md mx-auto">
                    We received your details. A coordinator will reach out within 48 hours.
                    In the meantime, thank you — truly.
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    className="btn-ghost mt-6"
                    data-testid="volunteer-reset"
                  >
                    Submit another response
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="card-soft space-y-5" data-testid="volunteer-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs uppercase tracking-[0.22em] text-slate-500">Full name</label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="input-field mt-2"
                        placeholder="Your name"
                        data-testid="volunteer-name-input"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.22em] text-slate-500">Email</label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="input-field mt-2"
                        placeholder="you@email.com"
                        data-testid="volunteer-email-input"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs uppercase tracking-[0.22em] text-slate-500">Phone (optional)</label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="input-field mt-2"
                        placeholder="Phone number"
                        data-testid="volunteer-phone-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.22em] text-slate-500">City</label>
                      <input
                        type="text"
                        value={form.city}
                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                        className="input-field mt-2"
                        placeholder="Your city"
                        data-testid="volunteer-city-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.22em] text-slate-500">I'm interested in</label>
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="volunteer-interests">
                      {INTEREST_OPTIONS.map((i) => {
                        const active = form.interests.includes(i);
                        return (
                          <button
                            type="button"
                            key={i}
                            onClick={() => toggleInterest(i)}
                            data-testid={`interest-${i.toLowerCase().replace(/\s+/g, "-").replace(/'/g, "")}`}
                            className={`rounded-full border px-4 py-2 text-sm transition-all ${
                              active
                                ? "border-[#2E8B8B] bg-[#E6F0F2] text-[#0A2540]"
                                : "border-slate-200 text-slate-600 hover:border-[#2E8B8B]"
                            }`}
                          >
                            {i}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.22em] text-slate-500">Availability</label>
                    <select
                      value={form.availability}
                      onChange={(e) => setForm({ ...form, availability: e.target.value })}
                      className="input-field mt-2"
                      data-testid="volunteer-availability-select"
                    >
                      <option value="">Select your availability</option>
                      <option>Weekday mornings</option>
                      <option>Weekday evenings</option>
                      <option>Weekends</option>
                      <option>Occasional events</option>
                      <option>Flexible</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.22em] text-slate-500">Anything you'd like us to know?</label>
                    <textarea
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      rows={4}
                      className="input-field mt-2 resize-none"
                      placeholder="A short note about your hopes, skills, or questions."
                      data-testid="volunteer-message-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full disabled:opacity-70"
                    data-testid="volunteer-submit-button"
                  >
                    <Send className="h-4 w-4" />
                    {submitting ? "Sending…" : "Send my application"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
